/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import session from '../../../../../share/session';
import config from '../../../../../share/config';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTE: 'execute',
  SUCCESS: 'success',
  FAILED_EXECUTE: 'failedExecute',
  FAILED: 'failed'
};

/** ノーマルモード定義 */
const NORMAL_MODE = {
  LABEL: 'label_normal_mode',
  DATA: [
    '00'
  ],
};
/** チェックモード定義 */
const CHECK_MODE = {
  LABEL: 'label_check_mode',
  DATA: [
    '01', '02', '03', '04', '05',
    '06', '07', '08', '09', '0A'
  ],
};

export default {
  name: 'requirementSpBC2',
  data() {
    return {
      phase: 'start',
      isLoading: false,
      isDisabled: false,
      displayInfoCurrentMode: '', // 現在のモードメッセージ
      displayInfoShiftMode: '',   // 移行先モードメッセージ
      modal_special_requirement_BC2: false
    };
  },

  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.EXECUTE:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    // 初期化メソッドを呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化メソッド */
    async init() {
      this.modal_special_requirement_BC2 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // データ読み出し処理
      this.readPostGet();
      // 移行先モード情報
      this.shiftModeInfo = {};
    },

    //----- htmlイベントメソッド -----------------------------------------------//

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      this.$emit('close-wizard');
      this.modal_special_requirement_BC2 = false;
    },

    /** 実行画面OKボタンクリックイベント */
    async clickExecutePostGet() {
      switch (this.shiftModeInfo) {
        case NORMAL_MODE:
          // 実行(ノーマルモード遷移)メソッドを呼び出す
          await this.executeNormalPostGet();
          break;
        case CHECK_MODE:
          // 実行(チェックモード遷移)メソッドを呼び出す
          await this.executeCheckPostGet();
          break;
        default:
          break;
      }
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** 実行表示メソッド */
    showExecute() {
      this.phase = PHASE.EXECUTE;
    },

    /** 成功表示メソッド */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** 失敗表示メソッド */
    showFailedExecute() {
      this.phase = PHASE.FAILED_EXECUTE;
    },

    /** 通信エラー表示メソッド */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /**
     * データ読み出しメソッド
     */
    async readPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'read',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 現在と移行先モード名を画面メッセージとして設定
            if (NORMAL_MODE.DATA.includes(response.data.user_data.mode)) {
              // 現在: ノーマルモード
              this.displayInfoCurrentMode = this.$t(`individual.${NORMAL_MODE.LABEL}_BC2`);
              this.displayInfoShiftMode = this.$t(`individual.${CHECK_MODE.LABEL}_BC2`);
              this.shiftModeInfo = CHECK_MODE;
              // 実行画面に遷移
              this.showExecute();
            } else if (CHECK_MODE.DATA.includes(response.data.user_data.mode)) {
              // 現在: チェックモード
              this.displayInfoCurrentMode = this.$t(`individual.${CHECK_MODE.LABEL}_BC2`);
              this.displayInfoShiftMode = this.$t(`individual.${NORMAL_MODE.LABEL}_BC2`);
              this.shiftModeInfo = NORMAL_MODE;
              // 実行画面に遷移
              this.showExecute();
            } else {
              // [ノーマル] or [チェック]ではない場合、通信エラー画面に遷移
              this.showUnexpectedError();
            }
          } else {
            // [成功以外]
            // 通信エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 実行(ノーマルモード遷移)メソッド
     */
    async executeNormalPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'execute_normal'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 成功画面に遷移
            this.showSuccess();
          } else {
            // [成功以外]
            // 失敗画面に遷移
            this.showFailedExecute();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 実行(チェックモード遷移)メソッド
     */
    async executeCheckPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'execute_check'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // セキュリティアクセス判定メソッドを呼び出す
            await this.secAccessPostGet();
          } else {
            // [成功以外]
            // 通信エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 実行(セキュリティアクセス判定)メソッド
     */
    async secAccessPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'sec_access'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 実行(チェックモード遷移: 特殊セッション遷移)メソッドを呼び出す
            await this.executeCheckSpecialPostGet();
          } else {
            // [成功以外]
            // 終了メソッドを呼び出す
            await this.exitPostGet();
            // 通信エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 実行(チェックモード遷移: 特殊セッション遷移)メソッド
     */
    async executeCheckSpecialPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'execute_check_special'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 成功画面に遷移
            this.showSuccess();
          } else {
            // [成功以外]
            // 失敗画面に遷移
            this.showFailedExecute();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で通信バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 通信エラー画面に遷移
      this.showFailed();
    },

  }
};